// src/pages/finance-portal/components/PlaidLinkButton.js
// Plaid Link integration button - fetches link token and opens Plaid Link modal

import React, { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Button, CircularProgress } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

export default function PlaidLinkButton({ onSuccess, onExit, buttonText = 'Connect Bank Account', variant = 'contained', fullWidth = false, disabled = false }) {
    const [linkToken, setLinkToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchLinkToken = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.post('/api/finance/plaid/create-link-token');
            if (response.data.success) {
                setLinkToken(response.data.link_token);
            } else {
                setError(response.data.message || 'Failed to initialize bank connection');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to initialize bank connection');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLinkToken();
    }, [fetchLinkToken]);

    const onPlaidSuccess = useCallback((publicToken, metadata) => {
        if (onSuccess) {
            onSuccess(publicToken, metadata);
        }
    }, [onSuccess]);

    const onPlaidExit = useCallback((err, metadata) => {
        if (err) {
            setError(err.display_message || err.error_message || 'Connection was cancelled');
        }
        if (onExit) {
            onExit(err, metadata);
        }
    }, [onExit]);

    const config = {
        token: linkToken,
        onSuccess: onPlaidSuccess,
        onExit: onPlaidExit,
    };

    const { open, ready } = usePlaidLink(config);

    const handleClick = () => {
        if (error || !linkToken) {
            // Re-fetch token if there was an error
            fetchLinkToken();
            return;
        }
        open();
    };

    return (
        <Button
            variant={variant}
            onClick={handleClick}
            disabled={disabled || loading || (!ready && !error)}
            fullWidth={fullWidth}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AccountBalanceIcon />}
            sx={{
                fontFamily,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                fontSize: '0.95rem',
                bgcolor: variant === 'contained' ? ACCENT : 'transparent',
                color: variant === 'contained' ? '#fff' : ACCENT,
                border: variant === 'outlined' ? `2px solid ${ACCENT}` : 'none',
                '&:hover': {
                    bgcolor: variant === 'contained' ? '#16a34a' : 'rgba(34, 197, 94, 0.08)',
                },
                '&:disabled': {
                    bgcolor: variant === 'contained' ? '#94a3b8' : 'transparent',
                    color: '#94a3b8',
                },
            }}
        >
            {loading ? 'Initializing...' : error ? 'Retry Connection' : buttonText}
        </Button>
    );
}
